import {
  useState,
  useContext,
  createContext,
  useCallback,
  useRef,
} from 'react';

import type { ModalPropType, ToastPropType } from '@kluein/klue-ui';

type UIModalType = {
  isOpen: boolean;
  open: (args: ModalPropType) => void;
  close: () => void;
  props: ModalPropType;
};

export type ToastItem = {
  id: string;
  props: ToastPropType;
};

type UIToastType = {
  /** @deprecated Use `toasts` array instead */
  isOpen: boolean;
  /** @deprecated Use `toasts` array instead */
  props: ToastPropType | null;
  /** Array of active toasts for stacking */
  toasts: ToastItem[];
  /** Opens a toast and returns its ID */
  open: (args: ToastPropType) => string;
  /** Closes a specific toast by ID, or closes the most recent if no ID provided.
   * Set skipOnClose to true to prevent calling the toast's onClose callback (for programmatic closes). */
  close: (id?: string, skipOnClose?: boolean) => void;
};

export const UIModalIntialValues = {
  isOpen: false,
  props: {},
  open: () => null,
  close: () => null,
};

export const UIToastIntialValues: UIToastType = {
  isOpen: false,
  props: null,
  toasts: [],
  open: () => '',
  close: () => null,
};

// If more context added to the UIType,
// it may be better splitting into separate files
type UIType = {
  modal: UIModalType;
  toast: UIToastType;
};

export const initialValue: UIType = {
  modal: UIModalIntialValues,
  toast: UIToastIntialValues,
};

export const appUIContext = createContext<UIType>(initialValue);

export const useAppUIProvider = () => {
  const [modalState, setModalState] =
    useState<UIModalType>(UIModalIntialValues);
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const toastIdCounter = useRef(0);

  const openModal = useCallback(
    (modalProps: ModalPropType) => {
      setModalState({
        ...modalState,
        isOpen: true,
        props: modalProps || {},
      });
    },
    [modalState],
  );

  const closeModal = () => {
    setModalState({
      ...modalState,
      isOpen: false,
      props: {},
    });
  };

  const openToast = useCallback((toastProps: ToastPropType): string => {
    const id = `toast-${++toastIdCounter.current}`;
    setToasts((current) => [...current, { id, props: toastProps }]);
    return id;
  }, []);

  const closeToast = useCallback((id?: string, skipOnClose?: boolean) => {
    setToasts((current) => {
      if (id) {
        // Close specific toast
        const toast = current.find((t) => t.id === id);
        if (!skipOnClose && toast?.props?.onClose) {
          toast.props.onClose();
        }
        return current.filter((t) => t.id !== id);
      } else {
        // Close most recent toast (for backwards compatibility)
        if (current.length > 0) {
          const lastToast = current[current.length - 1];
          if (!skipOnClose && lastToast?.props?.onClose) {
            lastToast.props.onClose();
          }
          return current.slice(0, -1);
        }
        return current;
      }
    });
  }, []);

  // Backwards compatibility: derive single toast state from array
  const latestToast = toasts[toasts.length - 1];

  return {
    modal: {
      ...modalState,
      open: openModal,
      close: closeModal,
    },
    toast: {
      isOpen: toasts.length > 0,
      props: latestToast?.props || null,
      toasts,
      open: openToast,
      close: closeToast,
    },
  };
};

/**
 * @name useAppUIContext
 * @description Access UI global context to control modals and other layout elements
 * @example
 * const {modal} = useAppUIContext();
 * modal.open({image: 'http://...'})
 */
export const useAppUIContext = () => {
  return useContext(appUIContext);
};
